// Venues (spec/15 P-05): the public venue directory with a search over name and area and an "open now" filter, as a
// grid of cards. Over `GET /v1/public/venues`. A visitor may browse it signed out; a player sees it in their frame.

import { useTranslation } from '@clavian/i18n/react';
import { Button, EmptyState, SegmentedControl, Skeleton, TextField } from '@clavian/ui-web';
import { type CSSProperties, useMemo, useState, type ReactNode } from 'react';

import { usePublicVenues } from '../api/queries';
import { LoadFailure, LoadingRegion } from '../shell/feedback';
import { NavIcon } from '../shell/nav-icons';
import { Page } from '../shell/Page';
import { VenueCard } from './VenueCard';

export function VenuesPage(): ReactNode {
  const { t } = useTranslation('player');
  const [search, setSearch] = useState('');
  const [show, setShow] = useState<'all' | 'open'>('all');
  const venues = usePublicVenues(show === 'open' ? { openNow: true } : {});

  const term = search.trim().toLowerCase();
  const shown = useMemo(() => {
    const all = venues.data ?? [];
    if (term === '') return all;
    return all.filter((venue) => {
      const where = [venue.address?.line1, venue.address?.locality, venue.address?.region].filter(Boolean).join(' ').toLowerCase();
      return venue.name.toLowerCase().includes(term) || where.includes(term);
    });
  }, [venues.data, term]);

  return (
    <Page title={t('venues.title')} description={t('venues.subtitle')}>
      <div className="pw-venues__controls">
        <div className="pw-search">
          <TextField
            name="venue-search"
            label={t('venues.searchLabel')}
            placeholder={t('venues.searchPlaceholder')}
            type="search"
            enterKeyHint="search"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
        </div>
        <SegmentedControl
          label={t('venues.filter.legend')}
          value={show}
          onValueChange={setShow}
          options={[
            { value: 'all', label: t('venues.filter.all') },
            { value: 'open', label: t('venues.filter.open') },
          ]}
        />
      </div>

      {venues.isError ? (
        <LoadFailure title={t('venues.loadError')} description={t('venues.loadErrorBody')} error={venues.error} onRetry={() => void venues.refetch()} />
      ) : venues.isPending ? (
        <LoadingRegion className="pw-venue-grid">
          {[0, 1, 2, 3, 4, 5].map((key) => (
            <Skeleton key={key} height="15rem" />
          ))}
        </LoadingRegion>
      ) : shown.length === 0 ? (
        <EmptyState
          icon={<NavIcon name="venues" size={32} />}
          title={t('venues.empty')}
          description={t('venues.emptyBody')}
          action={
            <Button
              onClick={() => {
                setSearch('');
                setShow('all');
              }}
            >
              {t('venues.showAll')}
            </Button>
          }
        />
      ) : (
        <>
          <p className="pw-subtle" role="status" aria-live="polite">
            {t('venues.count', { count: shown.length })}
          </p>
          <ul className="pw-venue-grid">
            {shown.map((venue, index) => (
              <li key={venue.id} style={{ '--pw-i': Math.min(index, 12) } as CSSProperties}>
                <VenueCard venue={venue} />
              </li>
            ))}
          </ul>
        </>
      )}
    </Page>
  );
}
